import { Badge, Box, Button, Card, Group, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ConfirmationModal from '../Modals/ConfirmationModal';
import ErrorMessage from '../ErrorMessage';
import { formatDate } from '../KnowledgeBase/shared';
import Spinner from '../Spinner';
import BundleExportModal from './BundleExportModal';
import { ConnectorStatusBadge } from './shared';
import TransitionConfirm from './TransitionConfirm';
import { confirmFor, instanceLabel, nextTransitions, probePassed, transitionMessage } from './transitions';

// draft and disabled are the only statuses with no shopper-facing effect to lose; validated and
// live answer policy_denied if this ever reaches the server regardless.
const DELETABLE = ['draft', 'disabled'];

const pendingKey = (instance, transition) => `${instance.id}:${transition.to}`;

const hostOf = (url) => {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    // A draft is saved with whatever the URL field holds; show that rather than nothing.
    return url;
  }
};

export const rowModel = (instance) => {
  const probe = instance.lastProbeResult;
  const transition = instance.lastTransition;

  return {
    id: instance.id,
    contract: instanceLabel(instance) || '',
    description: instance.tool?.description || null,
    type: instance.type,
    status: instance.status,
    host: hostOf(instance.config?.request?.url),
    probe: probe ? `${probe.code || 'ok'} · ${formatDate(probe.probedAt || instance.lastProbeAt)}` : 'never',
    transition: transition
      ? `${transition.toStatus} since ${formatDate(transition.at)} · ${transition.userEmail || 'unknown user'}`
      : null,
  };
};

const KINDS = {
  tool: { label: 'Store tools', create: 'New store tool', empty: 'No store tools yet' },
  capability: {
    label: 'Platform capabilities',
    create: 'New platform capability',
    empty: 'No platform capabilities yet',
  },
};

export const kindOf = (instance) => (instance.tool ? 'tool' : 'capability');

const ConnectorsListScreen = ({
  apiBase,
  refreshKey,
  kind = 'tool',
  onKindChange,
  onCreate,
  onLoaded,
  onChanged,
  onEdit,
}) => {
  const fetch = useFetch();

  const [instances, setInstances] = useState();
  const [error, setError] = useState();
  const [confirm, setConfirm] = useState(null);
  // Keyed on the row and the destination, not the row alone: a status with two transitions would
  // otherwise spin both buttons on one click.
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [exportInstance, setExportInstance] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    let live = true;
    setInstances(undefined);
    setError(undefined);

    fetch(apiBase)
      .then((res) => {
        if (!live) return;
        const rows = res.instances || [];
        setInstances(rows);
        onLoaded(rows);
      })
      .catch((err) => {
        if (live) {
          setError(getErrorMessage(err, 'Could not load the connectors.'));
        }
      });

    return () => {
      live = false;
    };
  }, [fetch, apiBase, refreshKey, onLoaded]);

  const move = (instance, to) =>
    fetch(`${apiBase}/${instance.id}/transition`, { method: 'POST', body: JSON.stringify({ to }) }).then(() => {
      notifications.show({
        title: 'Connector updated',
        message: transitionMessage(to, instance),
        color: 'success',
      });
      onChanged();
    });

  const pick = (instance, transition) => {
    setActionError(null);
    if (transition.confirm) {
      setConfirm({ ...confirmFor(transition.to, instance), action: () => move(instance, transition.to) });
      return;
    }

    setPending(pendingKey(instance, transition));
    move(instance, transition.to)
      .catch((err) => setActionError(getErrorMessage(err, 'The connector could not be moved.')))
      .finally(() => setPending(null));
  };

  const runDelete = (close) => {
    setDeleting(true);
    setDeleteError(null);

    fetch(`${apiBase}/${deleteTarget.id}`, { method: 'DELETE' })
      .then(() => {
        onChanged();
        close();
      })
      .catch((err) => setDeleteError({ message: getErrorMessage(err, 'The connector could not be deleted.') }))
      .finally(() => setDeleting(false));
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  // A Mantine Tooltip never fires on a disabled control, so the blocked button sits in a wrapper
  // that still receives the pointer events.
  const transitionButton = (instance, transition) => {
    const blocked = transition.needsProbe && !probePassed(instance);
    const button = (
      <Button
        key={transition.to}
        size="xs"
        variant="light"
        color={transition.to === 'disabled' ? 'danger' : undefined}
        disabled={blocked}
        loading={pending === pendingKey(instance, transition)}
        onClick={() => pick(instance, transition)}
      >
        {transition.label}
      </Button>
    );

    if (!blocked) return button;

    return (
      <Tooltip key={transition.to} label="Run a passing probe first" withArrow>
        <Box>{button}</Box>
      </Tooltip>
    );
  };

  const count = (value) => (instances ? ` (${instances.filter((row) => kindOf(row) === value).length})` : '');
  const shown = (instances || []).filter((row) => kindOf(row) === kind);

  const header = (
    <Group justify="space-between">
      <SegmentedControl
        size="xs"
        value={kind}
        onChange={onKindChange}
        data={Object.entries(KINDS).map(([value, { label }]) => ({ value, label: `${label}${count(value)}` }))}
      />
      <Button size="xs" onClick={() => onCreate(kind)}>
        {KINDS[kind].create}
      </Button>
    </Group>
  );

  if (error)
    return (
      <Stack gap="xs" mt="sm">
        {header}
        <ErrorMessage message={error} />
      </Stack>
    );
  if (!instances)
    return (
      <Stack gap="xs" mt="sm">
        {header}
        <Spinner />
      </Stack>
    );

  return (
    <Stack gap="xs" mt="sm">
      {header}
      {actionError && <ErrorMessage message={actionError} />}
      {!shown.length && <Text c="dimmed">{KINDS[kind].empty}</Text>}

      {shown.map((instance) => {
        const row = rowModel(instance);

        return (
          <Card key={row.id} withBorder padding="xs">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text ff="monospace" fw={600}>
                    {row.contract}
                  </Text>
                  <Badge variant="light" color="gray">
                    {row.type}
                  </Badge>
                  <ConnectorStatusBadge status={row.status} />
                  <Text size="sm">{row.host}</Text>
                </Group>
                {row.description && (
                  <Text size="xs" c="dimmed">
                    {row.description}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  Last probe {row.probe}
                </Text>
                {row.transition && (
                  <Text size="xs" c="dimmed">
                    {row.transition}
                  </Text>
                )}
              </Stack>

              <Group gap="xs" wrap="nowrap">
                {nextTransitions(row.status).map((transition) => transitionButton(instance, transition))}
                <Button variant="default" size="xs" onClick={() => onEdit(instance)}>
                  {row.status === 'live' ? 'View' : 'Edit'}
                </Button>
                <Button variant="default" size="xs" onClick={() => setExportInstance(instance)}>
                  Export
                </Button>
                {DELETABLE.includes(row.status) && (
                  <Button variant="light" color="danger" size="xs" onClick={() => setDeleteTarget(instance)}>
                    Delete
                  </Button>
                )}
              </Group>
            </Group>
          </Card>
        );
      })}

      {confirm && (
        <TransitionConfirm confirm={confirm} onClose={() => setConfirm(null)} onDone={() => setConfirm(null)} />
      )}

      {exportInstance && (
        <BundleExportModal opened onClose={() => setExportInstance(null)} apiBase={apiBase} ids={[exportInstance.id]} />
      )}

      {deleteTarget && (
        <ConfirmationModal
          show
          close={closeDelete}
          confirm={runDelete}
          loading={deleting}
          error={deleteError}
          message={`Delete the connector ${instanceLabel(deleteTarget)}? This cannot be undone.`}
        />
      )}
    </Stack>
  );
};

export default ConnectorsListScreen;
